import { useTranslation } from 'react-i18next';
import { useAuthData, useUserPermissions, useUserRoles } from '@anytag/auth';
import { AllUserDetail } from '@anytag/graphql';
import { TableCell } from '@anytag/shared-atoms';
import { useTranslateCountry } from '@anytag/shared-hooks';
import { SpreadButton } from '@anytag/shared-molecules';
import { formatIntNumber, getUserRoleString } from '@anytag/shared-utils';
import { ColumnDef } from '@tanstack/react-table';

interface Props {
  openConfirmModal: (data: { id: number; name: string; email?: string } | null) => void;
}

export const useUsersTable = ({ openConfirmModal }: Props) => {
  const { t } = useTranslation();
  const { auth } = useAuthData();
  const { tc } = useTranslateCountry();
  const { hideDeleteUserBtn, hideEditUserBtn } = useUserPermissions();
  const { isAdminStaffTalentAgencyPartner, isPartner } = useUserRoles();

  // TA, PA, Admin and Staff can delete if custom permissions allow it
  const hasDeleteOption = isAdminStaffTalentAgencyPartner && !hideDeleteUserBtn;
  const columns: ColumnDef<AllUserDetail>[] = [
    {
      accessorKey: 'name',
      header: t<string>('HeaderColumn.Name'),
      cell: ({ row }) => {
        const cannotEditUsers = hideEditUserBtn && !isPartner && row.original.id !== auth.userId; // all Partners can edit (BE will control users list), also user can always edit himself

        return (
          <div className="account-name-column">
            <TableCell.Link
              to="/user/$id"
              params={{ id: row.original.id.toString() }}
              label={row.original.name}
              disabled={cannotEditUsers}
              css={row.original.id === auth.userId ? { fontWeight: 600 } : {}}
            />
            <SpreadButton
              collapseFrom={0}
              moreButtonProps={{ disabled: hideDeleteUserBtn && cannotEditUsers }}
              options={[
                {
                  label: t('Edit'),
                  to: '/user/$id',
                  params: { id: row.original.id.toString() },
                  icon: 'edit',
                  hidden: cannotEditUsers,
                  tooltip: { help: t('Button.Edit') }
                },
                {
                  label: t('Delete'),
                  icon: 'delete',
                  hidden: !hasDeleteOption,
                  onClick: () =>
                    openConfirmModal({ id: row.original.id, name: row.original.name, email: row.original.email }),
                  tooltip: { help: t('Button.Delete') }
                }
              ]}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'email',
      header: t<string>('HeaderColumn.Email Address'),
      cell: ({ row }) => <TableCell.Text value={row.original.email} className="min-w-25" />
    },
    {
      accessorKey: 'country',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => <TableCell.Text value={tc(row.original.country.name)} className="min-w-25" />
    },
    {
      accessorKey: 'role',
      header: t<string>('HeaderColumn.Role'),
      cell: ({ row }) => (
        <TableCell.Text value={t(`Option.${getUserRoleString(row.original.role)}`)} className="min-w-25" />
      )
    },
    {
      accessorKey: 'advertisersCount',
      header: t<string>('HeaderColumn.Advertiser'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatIntNumber(row.original.advertisersCount)} className="min-w-25" />
      )
    }
  ];

  return { columns };
};
